// Centralized service catalog for EntoRid.
// Each entry powers the /services grid + its own /services/:slug detail page.

export const CATEGORIES = [
  { id: 'termite', label: 'Termite Solutions', accent: 'from-amber-500 to-orange-600' },
  { id: 'general', label: 'General Pest Control', accent: 'from-emerald-500 to-green-600' },
  { id: 'specialized', label: 'Specialized Services', accent: 'from-violet-500 to-purple-600' },
  { id: 'sectors', label: 'Other Service Sectors', accent: 'from-sky-500 to-blue-600' },
];

const WHY_CHOOSE_DEFAULT = [
  { title: 'Certified Professionals', desc: 'Government-licensed technicians with 15+ years of experience.' },
  { title: 'Eco-Friendly Chemicals', desc: 'Only ISI / WHO-approved, odourless, biodegradable formulations.' },
  { title: 'Fast Response Time', desc: 'Same-day appointment slots across Uttarakhand.' },
  { title: 'Affordable Pricing', desc: 'Transparent quotes — no hidden charges, ever.' },
  { title: 'Guaranteed Results', desc: 'Free re-treatment if pests return within warranty period.' },
];

const SAFETY_DEFAULT = [
  { title: 'Family-Safe', desc: 'Completely safe for children and elderly after 2-hour drying time.' },
  { title: 'Pet-Safe', desc: 'Cats, dogs and other pets can return to the space the same day.' },
  { title: 'Govt-Approved Chemicals', desc: 'Only CIB-registered, WHO-tested pesticides used.' },
  { title: 'Odourless Application', desc: 'No lingering smell — no need to move out during treatment.' },
];

const makePricing = (basic, standard, premium) => [
  { name: 'Basic', price: basic, popular: false, features: ['Single session', '1-month warranty', 'Basic spray treatment', 'Post-treatment guidance'] },
  { name: 'Standard', price: standard, popular: true, features: ['2 sessions (15-day gap)', '3-month warranty', 'Gel + Spray combo', 'Free inspection', 'Free follow-up visit'] },
  { name: 'Premium', price: premium, popular: false, features: ['4 sessions quarterly', '1-year warranty', 'All treatment methods', 'Unlimited follow-ups', 'Priority WhatsApp support', 'Dedicated technician'] },
];

// ======================== SERVICES CATALOG ========================
export const SERVICES = [
  // ---------- TERMITE SOLUTIONS ----------
  {
    slug: 'anti-termite-treatment',
    category: 'termite',
    name: 'Anti Termite Treatment',
    tagline: 'Protect Your Property from Hidden Wood-Destroyers',
    shortDesc: 'Complete termite elimination with long-lasting chemical barrier treatment.',
    heroImage: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=1200&q=80',
    about: {
      what: 'Termites are small, social insects that feed on cellulose in wood and paper. They silently cause billions in property damage every year — often invisible until structural damage is severe.',
      danger: 'Termites weaken structural beams, furniture, door frames and wooden floors. Left untreated, infestations can make entire sections of your home unsafe to occupy.',
      common: ['Wooden furniture', 'Door & window frames', 'Floorboards', 'Walls with hidden mud tunnels', 'Attic / roof timbers', 'Paper stacks & books'],
    },
    signs: [
      'Small mud tubes on walls or foundations',
      'Hollow-sounding wood when tapped',
      'Discarded wings near windows after swarming',
      'Frass (termite droppings) that looks like sawdust',
      'Peeling paint or bubbling plaster',
      'Tight-fitting doors & windows that suddenly jam',
    ],
    process: [
      { title: 'Thorough Inspection', desc: 'Our certified technician identifies colonies, damage extent and entry points across your property.' },
      { title: 'Species Identification', desc: 'We detect whether you have subterranean, drywood or dampwood termites — each needs a distinct approach.' },
      { title: 'Chemical Barrier Treatment', desc: 'ISI-grade termiticide injected into soil, masonry holes & wood using professional drilling + spraying equipment.' },
      { title: 'Preventive Protection', desc: 'Final perimeter treatment around your property + detailed prevention guidance from our expert.' },
    ],
    pricing: makePricing('₹2,499', '₹4,999', '₹8,999'),
    faqs: [
      { q: 'How long does anti-termite treatment last?', a: 'A professional treatment typically lasts 5–10 years, depending on soil type and warranty plan.' },
      { q: 'Do I need to move out during treatment?', a: 'No. The process is odourless and you can remain at home — just avoid the treated area for 2 hours.' },
      { q: 'Are the chemicals used safe for kids & pets?', a: 'Yes, we use WHO-approved, low-toxicity termiticides that are safe once dry (within 2 hours).' },
      { q: 'What if termites return after treatment?', a: 'Under our Standard and Premium plans, we re-treat free of charge during the warranty period.' },
    ],
    seoKeywords: 'anti termite treatment, termite treatment near me, best termite control, pest control Uttarakhand',
  },
  {
    slug: 'pre-construction-anti-termite',
    category: 'termite',
    name: 'Pre-Construction Anti-Termite Treatment',
    tagline: 'Build on a Termite-Proof Foundation',
    shortDesc: 'Soil & foundation chemical barrier laid before construction begins — protects for 10 years.',
    heroImage: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&q=80',
    about: {
      what: 'Pre-construction anti-termite treatment (PCT) is a chemical soil-barrier applied to foundation trenches, plinth area, and refill soil BEFORE concrete is poured — giving your building lifetime termite immunity.',
      danger: 'Without PCT, sub-terranean termites can colonise beneath concrete slabs within 2–3 years of construction, causing irreversible structural compromise.',
      common: ['Foundation trenches', 'Plinth area', 'Sub-soil layers', 'Retaining walls', 'Column bases', 'Expansion joints'],
    },
    signs: [
      'N/A — PCT is preventive; applied before termites ever arrive',
      'Recommended for ALL new residential construction',
      'Mandatory for commercial buildings under IS 6313 standard',
    ],
    process: [
      { title: 'Site Survey', desc: 'Engineer visits your plot to measure trenches, plinth & foundation dimensions.' },
      { title: 'Pre-Plinth Spray', desc: 'Treat sides & bottom of foundation trenches with 5L termiticide emulsion per m².' },
      { title: 'Refill-Soil Treatment', desc: 'Layer-by-layer treatment of the soil placed back against the foundation.' },
      { title: 'Plinth & Perimeter Finish', desc: 'Final treatment before flooring + outer perimeter barrier up to 500mm from wall.' },
    ],
    pricing: makePricing('₹8 / sq ft', '₹12 / sq ft', '₹18 / sq ft'),
    faqs: [
      { q: 'When is the best time to apply PCT?', a: 'Immediately after foundation excavation and before concrete slab pouring — timing is critical.' },
      { q: 'How long does PCT protection last?', a: 'Up to 10 years under our Premium plan, as per IS 6313 Part II standards.' },
      { q: 'Is PCT mandatory by law?', a: 'Yes, for all government and commercial projects. Strongly recommended for residential too.' },
      { q: 'Can I see a completion certificate?', a: 'Absolutely — we provide a signed ISO-compliant certificate and warranty card.' },
    ],
    seoKeywords: 'pre construction termite treatment, PCT, IS 6313, soil treatment for termites',
  },
  {
    slug: 'post-construction-anti-termite',
    category: 'termite',
    name: 'Post-Construction Anti-Termite Treatment',
    tagline: 'Rescue Your Existing Property from Termite Attack',
    shortDesc: 'Targeted drill-and-inject treatment for buildings already showing termite activity.',
    heroImage: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=80',
    about: {
      what: 'Post-construction treatment uses precision drilling and chemical injection to eliminate active termite colonies within existing buildings and create a protective soil barrier.',
      danger: 'Active termite infestations can destroy 15% of a home\'s structural wood within 18 months if left untreated.',
      common: ['Ground-floor walls', 'Wooden flooring', 'Door & window frames', 'Stair risers', 'Kitchen cabinets', 'Bathroom vanities'],
    },
    signs: [
      'Visible mud tunnels on interior or exterior walls',
      'Frass (tiny wood pellets) near wooden furniture',
      'Hollow wood when tapped or prodded',
      'Termite swarmers during monsoon',
      'Bubbling or peeling wall paint',
      'Squeaky or sagging floorboards',
    ],
    process: [
      { title: 'Detailed Inspection', desc: 'Mapping infestation zones using moisture meters and thermal imaging.' },
      { title: 'Drill Holes Grid', desc: '12mm holes drilled at 300mm intervals along walls at plinth level.' },
      { title: 'Chemical Injection', desc: 'Termiticide pumped under pressure into each hole — creates a continuous barrier.' },
      { title: 'Seal & Finish', desc: 'Holes sealed with white cement + final perimeter spray for maximum protection.' },
    ],
    pricing: makePricing('₹2,999', '₹5,999', '₹11,999'),
    faqs: [
      { q: 'Will drilling damage my walls?', a: 'No — we use 12mm bits, sealed neatly with matching cement. Virtually invisible once dried.' },
      { q: 'How soon will termites die?', a: 'The colony collapses within 7–14 days. You\'ll stop seeing live termites within a week.' },
      { q: 'Is the chemical safe for my family?', a: 'Yes, we use odourless termiticides safe once dried (2 hours). No need to vacate.' },
      { q: 'What warranty do I get?', a: '1-year (Basic), 3-year (Standard), 5-year (Premium) — with free re-treatment if termites return.' },
    ],
    seoKeywords: 'post construction termite treatment, drill and inject termite, termite control existing building',
  },
  {
    slug: 'reticulation-system',
    category: 'termite',
    name: 'Reticulation System Installation',
    tagline: 'Permanent Termite Protection Without Drilling',
    shortDesc: 'Advanced piped system — inject termiticide any time without disturbing your building.',
    heroImage: 'https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=1200&q=80',
    about: {
      what: 'A reticulation system is a network of perforated pipes installed BEFORE construction, allowing future termiticide top-ups without drilling walls. Considered the gold standard of modern termite protection.',
      danger: 'Traditional treatments lose effectiveness after 5–7 years. A reticulation system allows easy re-application, ensuring lifetime protection.',
      common: ['Plot boundary', 'Under-slab perimeter', 'Inside cavity walls', 'Foundation footings'],
    },
    signs: [
      'Recommended for any new construction seeking long-term protection',
      'Especially valuable for high-end villas, schools, hospitals',
      'Mandatory for properties near forests or older infested areas',
    ],
    process: [
      { title: 'Site Design', desc: 'Engineer maps pipe routes across your plot based on building blueprint.' },
      { title: 'Pipe Installation', desc: 'Food-grade, micro-perforated HDPE pipes laid at foundation + perimeter depth.' },
      { title: 'Access Points', desc: 'Inlet valves discreetly placed at 4–6 accessible locations for easy top-up.' },
      { title: 'First Charge & Testing', desc: 'System pressure-tested and initial termiticide charge applied.' },
    ],
    pricing: makePricing('₹15 / sq ft', '₹22 / sq ft', '₹30 / sq ft'),
    faqs: [
      { q: 'How does re-charging work?', a: 'Our technician visits every 3–5 years, connects pump to inlet valves, and refills termiticide — takes 1 day.' },
      { q: 'What\'s the pipe lifespan?', a: 'HDPE pipes last 50+ years underground without corrosion.' },
      { q: 'Does it interfere with plumbing?', a: 'No — reticulation pipes are laid separately, 300mm away from any plumbing.' },
      { q: 'Is AMC required?', a: 'Not mandatory, but recommended Premium plan includes 4 refills over 20 years.' },
    ],
    seoKeywords: 'reticulation system, termite proofing, permanent termite solution',
  },

  // ---------- GENERAL PEST CONTROL ----------
  {
    slug: 'cockroach-control',
    category: 'general',
    name: 'Cockroach Control Treatment',
    tagline: 'Say Goodbye to Disease-Carrying Pests',
    shortDesc: 'Safe gel baiting + spray treatment eliminates all cockroach species in one session.',
    heroImage: 'https://images.unsplash.com/photo-1583256225800-5c1d5c2af9d0?w=1200&q=80',
    about: {
      what: 'Cockroaches are nocturnal scavengers that spread bacteria, trigger asthma and contaminate food. Common species in India: German, American, Brown-banded and Oriental.',
      danger: 'Cockroaches carry E. coli, Salmonella and Staphylococcus bacteria. Their droppings are a leading asthma trigger in children.',
      common: ['Kitchen cabinets', 'Bathroom drains', 'Behind refrigerators', 'Under sinks', 'Electrical boxes', 'Drawers & pantries'],
    },
    signs: [
      'Live roaches at night (especially in kitchens)',
      'Dark pepper-like droppings near food',
      'Musty / oily odour in cabinets',
      'Egg cases (oothecae) tucked in corners',
      'Smeared streaks along walls',
      'Dead roaches behind appliances',
    ],
    process: [
      { title: 'Hotspot Inspection', desc: 'Identifying harborage zones, egg cases and entry points.' },
      { title: 'Species Identification', desc: 'Different roaches need different bait — we match the treatment exactly.' },
      { title: 'Gel Bait + Spray', desc: 'Odourless gel applied to hidden crevices + residual spray along skirting and drains.' },
      { title: 'Follow-up & Monitoring', desc: 'Free 15-day callback visit to break the roach breeding cycle completely.' },
    ],
    pricing: makePricing('₹799', '₹1,599', '₹2,999'),
    faqs: [
      { q: 'How soon will I see results?', a: 'Cockroach activity drops by 90% within 48 hours. Full elimination within 7–10 days.' },
      { q: 'Is the gel safe if my child or pet touches it?', a: 'Gel is placed in out-of-reach crevices. Even if contacted, it causes no harm to humans in the quantities applied.' },
      { q: 'Will cockroaches come back?', a: 'Not from existing colonies. New roaches can enter from neighbours — our AMC plan prevents this.' },
      { q: 'Do I need to empty my kitchen?', a: 'No! Our gel + spray works with furniture in place. No packing required.' },
    ],
    seoKeywords: 'cockroach control, roach treatment, gel baiting, best pest control for kitchen',
  },
  {
    slug: 'rodent-control',
    category: 'general',
    name: 'Rodent Control Treatment',
    tagline: 'Humane & Effective Rat / Mouse Removal',
    shortDesc: 'Glue traps + baiting + entry-point sealing removes rodents permanently.',
    heroImage: 'https://images.unsplash.com/photo-1585159812596-fac104f2f069?w=1200&q=80',
    about: {
      what: 'Rats and mice are prolific breeders and disease-carriers. In India, the Black Rat, Norway Rat and House Mouse are the three most common household pests.',
      danger: 'Rodents spread Leptospirosis, Hantavirus and plague. They also chew through wiring — a leading cause of house fires.',
      common: ['Kitchen storage', 'Ceilings & attics', 'Behind walls', 'Garages & basements', 'Electrical conduits', 'Godowns'],
    },
    signs: [
      'Droppings (5–10mm dark pellets) in drawers and corners',
      'Gnaw marks on food packets or wood',
      'Scratching sounds in walls or ceilings at night',
      'Greasy rub marks along walls',
      'Chewed electrical wiring',
      'Ammonia-like urine smell',
    ],
    process: [
      { title: 'Burrow Mapping', desc: 'Locating active rat burrows, entry gaps and movement paths.' },
      { title: 'Trapping & Baiting', desc: 'Professional snap traps + tamper-proof bait stations in child/pet-safe locations.' },
      { title: 'Entry Point Sealing', desc: 'Sealing holes with steel wool + cement. Most rodents enter through 1cm gaps.' },
      { title: 'Follow-up Visit', desc: 'Free re-check after 7 days to remove captured rodents and re-bait.' },
    ],
    pricing: makePricing('₹999', '₹1,999', '₹3,499'),
    faqs: [
      { q: 'Will you remove dead rats?', a: 'Yes — we remove any captured rodents during follow-up visits. No extra charge.' },
      { q: 'Are the baits safe for my pets?', a: 'Bait stations are tamper-proof and placed where only rodents can access. 100% pet-safe.' },
      { q: 'How long does treatment take?', a: 'Full elimination: 7–14 days. Heavy infestations may need Premium plan with extended monitoring.' },
      { q: 'Can you stop new rats from coming?', a: 'Yes — our Premium plan includes quarterly visits and entry-point maintenance.' },
    ],
    seoKeywords: 'rodent control, rat treatment, mouse removal, pest control for godown',
  },
  {
    slug: 'ant-control',
    category: 'general',
    name: 'Ant Control Treatment',
    tagline: 'Stop the March — Colony-Level Elimination',
    shortDesc: 'Targeted gel baits eradicate entire ant colonies, not just visible workers.',
    heroImage: 'https://images.unsplash.com/photo-1615870216519-2f9fa575fa5c?w=1200&q=80',
    about: {
      what: 'Ants are social insects with colonies of 10,000 to 500,000. Spraying only kills workers — the queen keeps reproducing. Our gel-bait approach eliminates the entire colony.',
      danger: 'Fire ants and carpenter ants cause painful bites. Pharaoh ants contaminate food & medical areas. Carpenter ants damage wood structures.',
      common: ['Kitchen counters', 'Food pantries', 'Around sinks', 'Window frames', 'Garden patios', 'Wall cracks'],
    },
    signs: [
      'Visible ant trails leading to food sources',
      'Small piles of fine dust (from carpenter ants)',
      'Winged ants inside the home (future queens)',
      'Nests under stones or in potted plants',
      'Ants around sugar containers or pet food',
    ],
    process: [
      { title: 'Colony Trail Mapping', desc: 'Follow worker-ant trails back to the nest entry.' },
      { title: 'Species Identification', desc: 'Ghost ants, sugar ants, fire ants — each needs a specific bait type.' },
      { title: 'Gel Bait Application', desc: 'Workers carry bait back to queen — entire colony dies within 5–7 days.' },
      { title: 'Perimeter Spray', desc: 'Residual spray along windows, doors & wall skirting to prevent re-entry.' },
    ],
    pricing: makePricing('₹699', '₹1,299', '₹2,499'),
    faqs: [
      { q: 'Why is gel better than spray?', a: 'Spray only kills visible ants. Gel kills the queen and eliminates the whole colony.' },
      { q: 'How long until ants disappear completely?', a: '5–7 days for the full colony to die out after bait placement.' },
      { q: 'Is it safe for kitchen surfaces?', a: 'Yes — gel is food-grade and placed only in hidden cracks, not on counters.' },
      { q: 'What if new ants come from neighbours?', a: 'Our AMC plan includes quarterly re-treatments to maintain protection.' },
    ],
    seoKeywords: 'ant control, ant colony treatment, gel bait, kitchen pest control',
  },
  {
    slug: 'mosquito-fogging',
    category: 'general',
    name: 'Mosquito Control Fogging',
    tagline: 'Dengue-Free, Malaria-Free Living',
    shortDesc: 'Thermal + ULV fogging kills adult mosquitoes + larvicidal treatment eliminates breeding.',
    heroImage: 'https://images.unsplash.com/photo-1599843347624-f956f92d0b1c?w=1200&q=80',
    about: {
      what: 'Mosquitoes in India transmit Dengue, Malaria, Chikungunya and Japanese Encephalitis. We combine adult-killing fogging with larvicidal treatment to break the life cycle.',
      danger: 'India reports over 150,000 dengue cases annually. Female Aedes mosquitoes feed primarily at dawn and dusk, posing year-round risk.',
      common: ['Stagnant water in pots', 'Terrace water tanks', 'Coolers', 'Open drains', 'Shaded garden areas', 'Basements'],
    },
    signs: [
      'Visible mosquito swarms at dawn / dusk',
      'Mosquito bites during indoor activity',
      'Larvae in stagnant water sources',
      'Family members reporting dengue-like symptoms',
    ],
    process: [
      { title: 'Breeding Site Survey', desc: 'Identify all stagnant water sources around your property.' },
      { title: 'Larvicidal Treatment', desc: 'Apply BTI granules to water sources — kills larvae without affecting aquatic life.' },
      { title: 'Thermal Fogging', desc: 'Outdoor thermal fog (WHO-approved pyrethroid) kills adult mosquitoes instantly.' },
      { title: 'ULV Indoor Spray', desc: 'Ultra-low-volume mist for indoors — targets hidden resting mosquitoes.' },
    ],
    pricing: makePricing('₹899', '₹1,799', '₹3,299'),
    faqs: [
      { q: 'How long does protection last?', a: 'Indoor: 30–45 days. Outdoor: 15–21 days, depending on weather & rainfall.' },
      { q: 'Is the chemical safe for my garden plants?', a: 'Yes, our formulations are plant-safe and biodegradable within 48 hours.' },
      { q: 'Best time for treatment?', a: 'Pre-monsoon (May–June) and post-monsoon (Sept) are ideal to break breeding cycles.' },
      { q: 'Will I see dead mosquitoes?', a: 'Yes, and mosquito activity drops 90% within 24 hours.' },
    ],
    seoKeywords: 'mosquito fogging, dengue prevention, mosquito control, thermal fogging',
  },
  {
    slug: 'bed-bug-removal',
    category: 'general',
    name: 'Bed Bug Removal Treatment',
    tagline: 'Sleep Peacefully — 100% Bed Bug Elimination',
    shortDesc: 'Heat + chemical combo removes bed bugs from mattresses, seams and furniture.',
    heroImage: 'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?w=1200&q=80',
    about: {
      what: 'Bed bugs are small, reddish-brown insects that feed on human blood at night. They hide in mattress seams, headboards, sofa folds and tiny cracks — multiplying rapidly into severe infestations.',
      danger: 'Bed bug bites cause itchy welts, allergic reactions, sleeplessness and anxiety. Untreated infestations spread to every room and to neighbours, making elimination extremely difficult.',
      common: ['Mattress seams & tags', 'Bed frames & headboards', 'Sofa cushions & folds', 'Behind wallpaper & posters', 'Curtain rods & wall cracks', 'Luggage & travel bags'],
    },
    signs: [
      'Itchy red bite marks in a line or cluster on skin',
      'Dark blood spots or fecal stains on bed sheets',
      'Tiny shed skins around mattress edges',
      'Sweet, musty odour in the bedroom',
      'Live bed bugs spotted in mattress seams at night',
      'Eggs (1mm white) tucked in furniture cracks',
    ],
    process: [
      { title: 'Deep Inspection', desc: 'Trained technician inspects every seam, crack and crevice using bright LED probes to locate all harborage points.' },
      { title: 'Steam / Heat Treatment', desc: 'High-temperature steam kills bed bugs and eggs on mattresses, sofas and curtains — without chemicals.' },
      { title: 'Residual Chemical Spray', desc: 'WHO-approved residual insecticide applied to cracks, joints and base boards for 3-month protection.' },
      { title: 'Free Follow-up', desc: 'Mandatory second visit after 15 days to break the egg-hatch cycle and guarantee 100% elimination.' },
    ],
    pricing: makePricing('₹1,499', '₹2,999', '₹5,499'),
    faqs: [
      { q: 'Will one treatment kill all bed bugs?', a: 'Bed bug eggs are protected from spray. Our Standard / Premium plans include a mandatory 15-day follow-up to kill newly hatched bugs.' },
      { q: 'Do I have to throw away my mattress?', a: 'No. Our steam + chemical combo saves mattresses, sofas and furniture in 95% of cases.' },
      { q: 'Are the chemicals safe for kids and pets?', a: 'Yes — we use WHO-approved, low-toxicity formulations that are safe once dry (2 hours).' },
      { q: 'How can I prevent bed bugs from returning?', a: 'Wash bedding at 60°C weekly, vacuum mattress seams, and avoid bringing used furniture indoors. Our Premium plan includes quarterly preventive visits.' },
    ],
    seoKeywords: 'bed bug removal, bed bug treatment, bed bug control near me, mattress pest control',
  },
  {
    slug: 'spider-control',
    category: 'general',
    name: 'Spider Control Treatment',
    tagline: 'Web-Free Corners, Balconies and Attics',
    shortDesc: 'Residual spray + web removal keeps corners, balconies and attics spider-free.',
    heroImage: 'https://images.unsplash.com/photo-1568526381923-caf3fd520382?w=1200&q=80',
    about: {
      what: 'Spiders enter homes searching for other insects and warm dark corners. While most Indian house spiders are harmless, their webs make spaces look unkempt — and some species (like brown recluse) can deliver painful bites.',
      danger: 'Spider bites can cause swelling, itching and allergic reactions. Cobwebs accumulate dust and dead insects, attracting more pests and worsening indoor air quality.',
      common: ['Ceiling corners & cornices', 'Balcony railings & lights', 'Attics & lofts', 'Behind wardrobes & shelves', 'Outdoor walls & garden sheds', 'Window frames & vents'],
    },
    signs: [
      'Visible cobwebs in corners and ceiling joints',
      'Live spiders spotted on walls or behind furniture',
      'Small egg sacs (white silk balls) in dark spots',
      'Increased spider activity at dusk or after rain',
      'Bite marks with localised swelling on skin',
      'Dead insects trapped in webs around the house',
    ],
    process: [
      { title: 'Web & Egg-Sac Removal', desc: 'Technician removes all cobwebs and egg sacs from corners, ceilings and outdoor walls.' },
      { title: 'Residual Spray', desc: 'Odourless residual insecticide applied to corners, baseboards and entry points — kills spiders on contact for 60+ days.' },
      { title: 'Outdoor Perimeter Treatment', desc: 'External walls, window frames and balconies treated to stop new spiders from entering.' },
      { title: 'Prevention Guidance', desc: 'Expert tips on sealing cracks, lighting changes and reducing other insects that attract spiders.' },
    ],
    pricing: makePricing('₹799', '₹1,499', '₹2,799'),
    faqs: [
      { q: 'Is spider spray safe for my family?', a: 'Yes — we use WHO-approved formulations safe for kids and pets once dry (2 hours).' },
      { q: 'How long does treatment last?', a: 'Indoor: 60–90 days. Outdoor: 30–45 days depending on rainfall and sunlight exposure.' },
      { q: 'Do you remove existing cobwebs?', a: 'Yes — web and egg-sac removal is included free with every treatment.' },
      { q: 'Will spiders come back?', a: 'New spiders may enter from outside. Our AMC plan includes quarterly visits to maintain a spider-free home.' },
    ],
    seoKeywords: 'spider control, spider treatment, cobweb removal, spider pest control near me',
  },
  {
    slug: 'lizard-control',
    category: 'general',
    name: 'Lizard Control Treatment',
    tagline: 'Keep Lizards Out — Odourless & Humane',
    shortDesc: 'Odourless repellent treatment keeps lizards out of rooms, kitchens and balconies.',
    heroImage: 'https://images.unsplash.com/photo-1591389703635-e15a07b842d7?w=1200&q=80',
    about: {
      what: 'House lizards (geckos) enter homes hunting insects attracted by lights. While not aggressive, their droppings spread Salmonella bacteria and they often startle family members, especially in kitchens and bedrooms.',
      danger: 'Lizard droppings can contaminate food surfaces. Lizards falling from ceilings cause panic, allergic reactions in sensitive individuals, and risk of bacterial contamination on cooking utensils.',
      common: ['Kitchen walls & ceilings', 'Behind cabinets & wall units', 'Near tube-lights & bulbs', 'Bathroom corners', 'Balcony walls', 'Window frames & curtains'],
    },
    signs: [
      'Lizards visible on walls at night',
      'Black & white droppings on shelves and floors',
      'Lizard eggs (small white ovals) behind furniture',
      'Increased activity near light fixtures',
      'Hissing or chirping sounds from walls',
      'Family members reporting frequent sightings',
    ],
    process: [
      { title: 'Entry Point Survey', desc: 'Inspector identifies cracks, vents and openings used by lizards to enter your home.' },
      { title: 'Repellent Application', desc: 'Odourless, food-safe lizard repellent applied to walls, corners, behind cabinets and around windows.' },
      { title: 'Egg Removal & Sanitisation', desc: 'Removal of lizard eggs and droppings, plus sanitisation of contaminated surfaces.' },
      { title: 'Prevention Plan', desc: 'Guidance on sealing gaps, lighting placement and reducing insect prey that attracts lizards.' },
    ],
    pricing: makePricing('₹899', '₹1,599', '₹2,899'),
    faqs: [
      { q: 'Is the repellent safe near kitchen surfaces?', a: 'Yes — our food-safe repellent is applied to walls, corners and edges, not on cooking surfaces. Completely odourless.' },
      { q: 'Do you kill the lizards?', a: 'No — our treatment is humane and repels lizards rather than killing them. They leave on their own within 7–10 days.' },
      { q: 'How long does treatment last?', a: 'Typical protection: 60–90 days. Our AMC plan provides year-round lizard-free living.' },
      { q: 'Will lizards return after treatment?', a: 'If entry points are sealed and outdoor lights reduced, recurrence is rare. AMC clients see virtually zero lizard activity.' },
    ],
    seoKeywords: 'lizard control, lizard repellent treatment, lizard removal, gecko control near me',
  },
  {
    slug: 'fly-control',
    category: 'general',
    name: 'Fly Control Treatment',
    tagline: 'Disease-Free Kitchens, Restaurants & Food Areas',
    shortDesc: 'Targeted fly eradication for homes, restaurants and food-prep areas.',
    heroImage: 'https://images.unsplash.com/photo-1605548109095-9c4b81e62333?w=1200&q=80',
    about: {
      what: 'House flies, fruit flies and drain flies are among the most common pests in Indian homes and restaurants. They breed in garbage, drains and rotting food — spreading bacteria onto every surface they land on.',
      danger: 'Flies transmit over 100 diseases including typhoid, cholera, dysentery, E. coli and Salmonella. A single fly can carry millions of bacteria on its body. For restaurants, fly sightings can fail FSSAI audits.',
      common: ['Kitchens & food prep areas', 'Garbage bins & dustbins', 'Drains & sinks', 'Fruit bowls & pantries', 'Compost & wet waste areas', 'Restaurant dining halls'],
    },
    signs: [
      'Flies hovering around food or kitchen counters',
      'Tiny fruit flies near ripe fruit or bins',
      'Drain flies emerging from sinks & bathroom drains',
      'Maggots in garbage or moist organic waste',
      'Dark fly specks on walls, light fixtures & curtains',
      'Customer complaints in restaurants & cafés',
    ],
    process: [
      { title: 'Breeding Site Survey', desc: 'Locate garbage zones, drains, compost and food residue spots where flies breed.' },
      { title: 'Source Sanitisation', desc: 'Drain gel treatment + bin sanitisation kills maggots and eliminates the breeding cycle.' },
      { title: 'Residual Spray & Baiting', desc: 'Targeted residual insecticide on resting areas + sweet fly baits in problem zones.' },
      { title: 'ULV Misting (Heavy Cases)', desc: 'Ultra-low-volume mist for instant knockdown in restaurants, cafés and large kitchens.' },
    ],
    pricing: makePricing('₹799', '₹1,499', '₹2,799'),
    faqs: [
      { q: 'Are the chemicals safe for kitchens and restaurants?', a: 'Yes — all our formulations are food-grade safe and FSSAI-compliant. Treatments are applied after hours for zero disruption.' },
      { q: 'How quickly will I see results?', a: 'Fly activity drops by 90% within 24 hours. Full breeding cycle eliminated within 7 days.' },
      { q: 'Do you provide FSSAI-compliant documentation?', a: 'Yes — for restaurants and cafés, every visit generates audit-ready digital reports.' },
      { q: 'How can I keep flies away long-term?', a: 'Daily garbage disposal, drain cleaning, fly screens on windows. Our AMC plan includes monthly fly-prevention visits.' },
    ],
    seoKeywords: 'fly control, fly treatment, fly pest control, restaurant fly control, drain fly removal',
  },
  {
    slug: 'silverfish-control',
    category: 'general',
    name: 'Silverfish Control Treatment',
    tagline: 'Protect Books, Clothes & Paper Archives',
    shortDesc: 'Protect books, clothes and paper archives from silverfish damage.',
    heroImage: 'https://images.unsplash.com/photo-1457369804613-52c61a468e7d?w=1200&q=80',
    about: {
      what: 'Silverfish are silver-grey, fish-shaped insects that thrive in dark, humid areas. They feed on starches, paper, glue, fabric and dead skin — silently damaging books, photographs, wallpaper and clothes stored in cupboards.',
      danger: 'Silverfish destroy irreplaceable items — old books, family albums, important documents, silk sarees and woollen clothing. They also trigger dust mite allergies and indicate underlying moisture problems in the home.',
      common: ['Bookshelves & libraries', 'Wardrobes & clothing stacks', 'Kitchen pantries', 'Bathroom corners & under sinks', 'Storage boxes & cardboard', 'Attics & damp basements'],
    },
    signs: [
      'Silver-grey, wingless insects with three tail bristles',
      'Tiny holes or notches in book pages and wallpaper',
      'Yellow stains or fecal pellets on clothes & shelves',
      'Shed skins in cupboards and dark corners',
      'Increased activity in humid monsoon months',
      'Damaged silk, cotton or starched fabrics',
    ],
    process: [
      { title: 'Moisture & Hotspot Survey', desc: 'Inspector identifies humid zones, infested shelves and entry points using moisture meter.' },
      { title: 'Targeted Spray Treatment', desc: 'Residual insecticide applied to cracks, baseboards, shelf undersides and behind furniture.' },
      { title: 'Dust Application', desc: 'Food-safe insecticidal dust placed in wall voids, behind cupboards and inside storage boxes.' },
      { title: 'Moisture Control Tips', desc: 'Expert recommendations on dehumidifying, ventilation and storage practices to prevent recurrence.' },
    ],
    pricing: makePricing('₹799', '₹1,499', '₹2,799'),
    faqs: [
      { q: 'Will treatment damage my books or clothes?', a: 'No — our spray and dust are applied to surrounding cracks and shelves, not directly on books or fabrics. 100% safe.' },
      { q: 'How long does treatment last?', a: 'Typical protection: 90–120 days. Premium AMC includes quarterly visits for year-round protection.' },
      { q: 'Are silverfish dangerous to humans?', a: 'They do not bite or sting, but their droppings can trigger allergies and they damage valuable belongings.' },
      { q: 'How can I prevent silverfish naturally?', a: 'Reduce indoor humidity below 50%, store paper in sealed boxes, and ventilate cupboards. Our team gives detailed prevention guidance.' },
    ],
    seoKeywords: 'silverfish control, silverfish treatment, silverfish removal, book pest control, wardrobe pest control',
  },

  // ---------- SPECIALIZED SERVICES ----------
  {
    slug: 'wood-borer',
    category: 'specialized',
    name: 'Wood Borer Control Treatment',
    tagline: 'Save Your Furniture from Silent Destruction',
    shortDesc: 'Targeted injection treatment saves antique and premium wooden items.',
    heroImage: 'https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=1200&q=80',
    about: {
      what: 'Powder-post beetles and wood-boring weevils lay eggs in cracks of seasoned wood. Larvae tunnel inside, turning furniture into honeycomb over 2–5 years.',
      danger: 'High-value antiques, plywood, teak & rosewood furniture can be destroyed from within. Damage often detected only after failure.',
      common: ['Plywood cabinets', 'Antique furniture', 'Wooden staircases', 'Ceiling rafters', 'Packing crates', 'Musical instruments'],
    },
    signs: [
      'Tiny round exit holes (1–2 mm) in furniture',
      'Fine sawdust piles below affected wood',
      'Hollow or weakened sections when tapped',
      'Fresh exit holes appearing seasonally',
      'Cracked or blistered varnish',
    ],
    process: [
      { title: 'Affected Area Mapping', desc: 'Inspector identifies active vs inactive infestations using pin-tester.' },
      { title: 'Injection Treatment', desc: 'Boron-based preservative injected into each exit hole with precision syringe.' },
      { title: 'Surface Spray', desc: 'Permethrin-based spray coats remaining surfaces to kill emerging adults.' },
      { title: 'Varnish Restoration Tips', desc: 'Guidance on post-treatment polishing to seal treated surfaces permanently.' },
    ],
    pricing: makePricing('₹1,299', '₹2,499', '₹4,999'),
    faqs: [
      { q: 'Will my antique furniture be damaged?', a: 'No — our injection technique is minimally invasive. Injection points are invisible after treatment.' },
      { q: 'How many treatments will I need?', a: 'Most cases: 1 treatment. Heavy infestations may require a follow-up after 6 months.' },
      { q: 'Is the chemical safe for indoor furniture?', a: 'Yes — boron-based preservatives are odourless and approved for indoor use.' },
      { q: 'Can I polish the furniture after treatment?', a: 'Yes, wait 72 hours then apply any varnish or polish as usual.' },
    ],
    seoKeywords: 'wood borer treatment, furniture pest control, powder post beetle, antique wood preservation',
  },
  {
    slug: 'honeybee-removal',
    category: 'specialized',
    name: 'Honeybee Removal Treatment',
    tagline: 'Safe, Humane, Eco-Friendly Hive Removal',
    shortDesc: 'Professional live-bee relocation to local apiaries — no killing of pollinators.',
    heroImage: 'https://images.unsplash.com/photo-1559963110-71b394e7494d?w=1200&q=80',
    about: {
      what: 'Honeybees are vital pollinators for Indian agriculture. We partner with local beekeepers to relocate hives alive rather than destroy them.',
      danger: 'Hives near living areas pose sting risks — especially dangerous for allergic individuals, children and the elderly. DIY removal often agitates bees.',
      common: ['Balcony corners', 'Window AC units', 'Roof edges', 'Tree branches near homes', 'Terrace water tanks', 'Exterior walls'],
    },
    signs: [
      'Visible honeycomb with thousands of bees',
      'Constant bee traffic near one spot',
      'Buzzing sounds from a wall or ceiling',
      'Honey dripping down walls',
      'Bee stings reported by family members',
    ],
    process: [
      { title: 'Safety Assessment', desc: 'Expert evaluates hive size, access, nearby people and allergic risks.' },
      { title: 'Suit Up & Smoke', desc: 'Bee-suit + smoker used to calm the colony without harming them.' },
      { title: 'Live Relocation', desc: 'Queen bee carefully transferred into a box; colony follows her within hours.' },
      { title: 'Hive Cleaning', desc: 'Residual honeycomb removed and area sanitized to prevent re-colonisation.' },
    ],
    pricing: makePricing('₹1,499', '₹2,799', '₹5,499'),
    faqs: [
      { q: 'Do you kill the bees?', a: 'No — we relocate live colonies to local apiaries whenever possible. Killing is a last resort.' },
      { q: 'Is it safe for my family during removal?', a: 'Absolutely. Our team cordons off a 10m safety radius and works during calm morning hours.' },
      { q: 'Will bees come back?', a: 'If the hive spot is sealed post-removal, recurrence is rare (less than 5%).' },
      { q: 'How long does removal take?', a: '2–4 hours depending on hive size and accessibility.' },
    ],
    seoKeywords: 'honeybee removal, live bee relocation, safe bee removal, pest control honey bees',
  },

  // ---------- SECTORS ----------
  {
    slug: 'residential',
    category: 'sectors',
    name: 'Residential Pest Control',
    tagline: 'Complete Home Protection for Your Family',
    shortDesc: 'Integrated plans covering all common household pests — one expert, one invoice.',
    heroImage: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=1200&q=80',
    about: {
      what: 'Our residential plans combine cockroach, ant, rodent, termite and mosquito treatments into a unified schedule tailored to your home size and risk profile.',
      danger: 'Most households face 3–5 types of pests simultaneously. Individual treatments alone leave gaps — integrated plans give complete coverage.',
      common: ['Apartments', 'Independent houses', 'Villas', 'Bungalows', 'Farmhouses', 'Row houses'],
    },
    signs: [
      'Ideal if you see multiple types of pests',
      'Recommended for homes with infants, elderly or pets',
      'Must-have for ground-floor apartments',
      'Crucial for homes near gardens or water bodies',
    ],
    process: [
      { title: 'Home Audit', desc: 'Comprehensive inspection covering all rooms, balconies and perimeter.' },
      { title: 'Custom Plan', desc: 'Tailored treatment schedule for your home\'s specific pest profile.' },
      { title: 'Scheduled Execution', desc: 'Treatments applied in logical order — heaviest pests first.' },
      { title: 'Year-Round Monitoring', desc: 'Quarterly check-ups + dedicated WhatsApp helpline for urgent issues.' },
    ],
    pricing: makePricing('₹2,999 / year', '₹4,999 / year', '₹8,999 / year'),
    faqs: [
      { q: 'What pests does residential plan cover?', a: 'Cockroaches, ants, rodents, mosquitoes, spiders, lizards, termites (add-on) and wood borers (add-on).' },
      { q: 'How often do you visit?', a: 'Basic: 2 visits/year, Standard: 4 visits/year, Premium: 6 visits/year + on-call service.' },
      { q: 'Can I upgrade mid-year?', a: 'Yes — pro-rated upgrade available any time.' },
      { q: 'What if pests return between visits?', a: 'Free re-treatment within 24 hours — guaranteed under all AMC plans.' },
    ],
    seoKeywords: 'residential pest control, home pest control, family safe pest control',
  },
  {
    slug: 'commercial',
    category: 'sectors',
    name: 'Commercial Pest Control',
    tagline: 'Hygiene that Protects Your Brand',
    shortDesc: 'FSSAI/ISO-compliant treatments for restaurants, offices, hotels, retail chains.',
    heroImage: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=1200&q=80',
    about: {
      what: 'Commercial pest control requires documented, audit-ready treatments that meet FSSAI, HACCP and ISO 22000 standards. We deliver compliance with minimal business disruption.',
      danger: 'One bad pest review can destroy restaurant ratings. One rodent sighting can fail your FSSAI audit. Professional documentation is non-negotiable.',
      common: ['Restaurants & cafés', 'Offices & coworking', 'Hotels & resorts', 'Retail stores', 'Hospitals & clinics', 'Educational institutions'],
    },
    commercialFields: undefined, // legacy key removed
    industryFields: [
      { label: 'Restaurants & Cafés', icon: 'UtensilsCrossed', desc: 'FSSAI-grade kitchen & dining-area protection.' },
      { label: 'Hotels & Resorts', icon: 'Hotel', desc: 'Discreet treatments during low-occupancy windows.' },
      { label: 'Offices & Coworking', icon: 'Building2', desc: 'Weekend service for zero workday disruption.' },
      { label: 'Hospitals & Clinics', icon: 'Stethoscope', desc: 'NABH-compliant odourless, child-safe chemicals.' },
      { label: 'Schools & Colleges', icon: 'GraduationCap', desc: 'Holiday-window treatments with safety SOPs.' },
      { label: 'Retail Stores', icon: 'ShoppingBag', desc: 'Stock-safe gel baiting & monitoring.' },
      { label: 'Shopping Malls', icon: 'ShoppingCart', desc: 'Common-area & food-court IPM programs.' },
      { label: 'Cloud Kitchens', icon: 'ChefHat', desc: 'High-frequency monitoring for delivery brands.' },
      { label: 'Banks & ATMs', icon: 'Landmark', desc: 'After-hours rodent & cockroach control.' },
      { label: 'Pharmacies', icon: 'Pill', desc: 'Stock-area treatments with audit logs.' },
      { label: 'Salons & Spas', icon: 'Scissors', desc: 'Odour-free, customer-safe applications.' },
      { label: 'Gyms & Fitness', icon: 'Dumbbell', desc: 'Sweat-zone bedbug & cockroach protocols.' },
      { label: 'Cinemas & Theatres', icon: 'Film', desc: 'Seat-area & concession-stand treatments.' },
      { label: 'Warehouses', icon: 'Warehouse', desc: 'Perimeter bait-station programs.' },
      { label: 'Food Processing', icon: 'Utensils', desc: 'HACCP-aligned facility programs.' },
      { label: 'Logistics & Couriers', icon: 'Truck', desc: 'Loading-bay & inventory pest control.' },
      { label: 'Hostels & PGs', icon: 'BedDouble', desc: 'Bedbug heat & spray treatments.' },
      { label: 'Cafés & Bakeries', icon: 'Coffee', desc: 'Sugar-pest & ant gel programs.' },
      { label: 'Pet Clinics', icon: 'PawPrint', desc: 'Animal-safe chemistry & timing.' },
      { label: 'Event Halls & Banquets', icon: 'Theater', desc: 'Pre-event sanitisation packages.' },
      { label: 'Other / Custom', icon: 'HelpCircle', desc: "Don't see your business? We customise for any premises." },
    ],
    signs: [
      'FSSAI / FSSC audit approaching',
      'Customer complaints of pest sightings',
      'Monthly food safety audits pending',
      'Staff reports of pest activity in pantry or storage',
    ],
    process: [
      { title: 'Compliance Audit', desc: 'Inspector documents current state with photos + creates FSSAI-compliant baseline report.' },
      { title: 'IPM Plan', desc: 'Integrated Pest Management strategy with prevention, monitoring & response protocols.' },
      { title: 'Scheduled Treatments', desc: 'Treatments applied after business hours to minimise disruption.' },
      { title: 'Digital Reporting', desc: 'Every visit logged with photos, technician notes & pest-sighting counts — audit-ready PDFs on demand.' },
    ],
    pricing: [],
    faqs: [
      { q: 'Do you provide FSSAI-compliant documentation?', a: 'Yes — every visit generates a digital log meeting FSSAI & HACCP documentation requirements.' },
      { q: 'Can treatments happen at night?', a: 'Yes. We work around your hours — most restaurants prefer 11pm-6am slots.' },
      { q: 'Do you treat large premises?', a: 'Absolutely — we service venues up to 50,000 sq ft with multi-technician teams.' },
      { q: 'What\'s response time for emergencies?', a: 'Less than 4 hours across all Uttarakhand cities under Premium plan.' },
    ],
    seoKeywords: 'commercial pest control, FSSAI pest control, restaurant pest control, office pest control',
  },
  {
    slug: 'industrial',
    category: 'sectors',
    name: 'Industrial Pest Control',
    tagline: 'Safeguarding Your Operations, Inventory & Workforce',
    shortDesc: 'Heavy-duty programs for factories, warehouses, godowns, food-processing plants.',
    heroImage: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&q=80',
    about: {
      what: 'Industrial sites require large-scale, engineered pest control using ULV foggers, bait stations and perimeter barriers to protect raw materials, finished goods and employee health.',
      danger: 'Rodent-chewed wiring causes 25% of warehouse fires. Pest contamination of raw materials can trigger product recalls costing crores.',
      common: ['Manufacturing plants', 'Warehouses & godowns', 'Cold storage', 'Food processing units', 'Pharmaceutical facilities', 'Textile mills'],
    },
    industryFields: [
      { label: 'Manufacturing Plants', icon: 'Factory', desc: 'Production-line safe IPM with shift-window service.' },
      { label: 'Warehouses & Godowns', icon: 'Warehouse', desc: 'Perimeter rodent stations every 20m.' },
      { label: 'Cold Storage', icon: 'Snowflake', desc: 'Low-temp safe chemistry & monitoring.' },
      { label: 'Food Processing Units', icon: 'ChefHat', desc: 'HACCP / FSSAI compliant treatments.' },
      { label: 'Pharmaceutical Plants', icon: 'FlaskConical', desc: 'WHO-GMP audit-ready pest logs.' },
      { label: 'Textile Mills', icon: 'Shirt', desc: 'Carpet beetle & moth control programs.' },
      { label: 'Chemical Plants', icon: 'TestTube', desc: 'Hazard-zone certified technicians.' },
      { label: 'Automotive Plants', icon: 'Car', desc: 'Paint-booth & assembly-line treatments.' },
      { label: 'Steel & Metal Plants', icon: 'Hammer', desc: 'High-heat-zone rodent control.' },
      { label: 'Cement Plants', icon: 'Construction', desc: 'Dust-zone perimeter & employee-area service.' },
      { label: 'Power Plants', icon: 'Zap', desc: 'Switchgear & cable-tray rodent protection.' },
      { label: 'Oil & Gas Refineries', icon: 'Fuel', desc: 'Explosion-proof bait stations & ATEX SOPs.' },
      { label: 'Paper & Pulp Mills', icon: 'FileText', desc: 'Stock-area silverfish & rodent programs.' },
      { label: 'Plastic & Rubber Units', icon: 'Boxes', desc: 'Raw-material storage protection.' },
      { label: 'Distilleries & Breweries', icon: 'Beer', desc: 'Fly & fermentation-zone control.' },
      { label: 'Electronics & Semiconductor', icon: 'Cpu', desc: 'Cleanroom-compatible monitoring.' },
      { label: 'Dairy & Cattle Farms', icon: 'Milk', desc: 'Fly, tick & rodent control.' },
      { label: 'Grain Silos & Storage', icon: 'Wheat', desc: 'Weevil & rat baiting programs.' },
      { label: 'Logistics & Distribution', icon: 'Truck', desc: 'Loading-dock & dispatch-zone IPM.' },
      { label: 'Shipping & Ports', icon: 'Ship', desc: 'Container-fumigation & dockside service.' },
      { label: 'Other / Custom', icon: 'HelpCircle', desc: "Don't see your industry? We design custom IPM programs." },
    ],
    signs: [
      'Large raw-material storage with pest risk',
      'Loading docks with frequent access',
      'History of audit findings for pest issues',
      'Employee safety & ISO compliance requirements',
    ],
    process: [
      { title: 'Facility Survey', desc: 'Engineer maps pest risk zones, infrastructure vulnerabilities and loading points.' },
      { title: 'Perimeter Barrier', desc: 'Tamper-proof rodent bait stations placed every 20m around perimeter.' },
      { title: 'ULV Fogging & Treatments', desc: 'Space fogging during maintenance shutdowns + targeted treatments in specific zones.' },
      { title: 'Audit-Ready Reports', desc: 'Monthly service reports with bait-station logs, pest counts & trend analysis.' },
    ],
    pricing: [],
    faqs: [
      { q: 'Can you service 24x7 operations?', a: 'Yes — our technicians work in shift-breaks, lunch hours and planned maintenance windows.' },
      { q: 'Do you handle warehouse audits?', a: 'Yes. We provide audit-ready pest trend data for ISO, FSSAI, WHO-GMP and US-FDA inspections.' },
      { q: 'What about heat-sensitive inventory?', a: 'We use targeted, low-temperature treatments in cold storages and pharma facilities.' },
      { q: 'Can you meet our insurance pest-control compliance?', a: 'Yes — we provide certificates meeting most commercial property insurance requirements.' },
    ],
    seoKeywords: 'industrial pest control, warehouse pest control, factory pest control, godown pest management',
  },
  {
    slug: 'amc',
    category: 'sectors',
    name: 'Annual Maintenance Contract (AMC)',
    tagline: 'Set It and Forget It — Year-Round Pest Freedom',
    shortDesc: 'Scheduled visits, priority support, unlimited re-treatments — save 30%+ vs one-off services.',
    heroImage: 'https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?w=1200&q=80',
    about: {
      what: 'Our AMC (Annual Maintenance Contract) bundles 4–12 scheduled visits per year with priority response, dedicated technician and unlimited free re-treatments if pests appear between visits.',
      danger: 'One-off treatments miss seasonal pest cycles. AMC ensures you\'re protected when monsoons bring mosquitoes, winters bring rodents and summers bring ants.',
      common: ['Perfect for busy families', 'Ideal for rental properties', 'Must for restaurants & offices', 'Essential for warehouses', 'Beneficial for schools & hospitals'],
    },
    signs: [
      'You\'ve had recurring pest issues in past',
      'You live in a pest-prone ground floor / basement',
      'Your building neighbours have pest problems',
      'You want audit compliance without the hassle',
      'You\'d rather not worry about pests ever',
    ],
    process: [
      { title: 'Onboarding Audit', desc: 'Detailed first-visit inspection + baseline treatment for existing pests.' },
      { title: 'Quarterly Schedule', desc: 'Pre-monsoon, monsoon, post-monsoon and winter visits — each targeting seasonal pests.' },
      { title: 'Priority Response', desc: 'Any pest sighting between visits → free technician within 48 hours (24hrs on Premium).' },
      { title: 'Year-End Review', desc: 'Annual report with pest trends + recommendations for renewal.' },
    ],
    pricing: makePricing('₹3,999 / year', '₹6,999 / year', '₹11,999 / year'),
    faqs: [
      { q: 'What\'s included in "unlimited re-treatments"?', a: 'Any covered pest appears between visits — we come treat at no extra cost. No quota, no fine print.' },
      { q: 'Can I pause AMC if I travel?', a: 'Yes — up to 3 months pause/year supported.' },
      { q: 'What happens if I move houses?', a: 'AMC transfers to new address free for one transfer per year.' },
      { q: 'Is there a cancellation penalty?', a: 'None after first 30 days. Pro-rated refund for unused visits.' },
    ],
    seoKeywords: 'pest control AMC, annual pest control, yearly pest maintenance, best AMC pest control',
  },
];

export const getServiceBySlug = (slug) => SERVICES.find(s => s.slug === slug);
export const getServicesByCategory = (catId) => SERVICES.filter(s => s.category === catId);
